import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import {
  ONE_AVERAGE_MONTH,
  ONE_AVERAGE_YEAR,
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
} from '@gem/services/date-service';

import { getTtlPeriodText } from 'APP/main/ChatView/ChatAutoCleanupSettings/ChatAutoCleanupSettings.utils';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';

export interface IChatAutoCleanupSettingsProps {
  groupId: string;
  value: number;
  onChange: (value: number) => void;
}

type TChatAutoCleanupSettingsItem = {
  key: string;
  title: string;
  value: number;
  isVisible: boolean;
};

interface IChatAutoCleanupSettingsPresenter {
  isAutoCleanupDisabled: boolean;
  groupId: string;
  group: Group | null;
  isDemoChatTtlOptionsEnabled: boolean;
  isCanEdit: boolean;
  settingsItems: TChatAutoCleanupSettingsItem[];
  setGroupId(groupId: string): void;
}

export function useChatAutoCleanupSettingsPresenter(
  params: IChatAutoCleanupSettingsProps
): IChatAutoCleanupSettingsPresenter {
  const presenter = useLocalObservable<IChatAutoCleanupSettingsPresenter>(() => ({
    groupId: params.groupId,

    currentTtlPeriodMs: params.value,

    get isDemoChatTtlOptionsEnabled(): boolean {
      return Entities.featuresStore.isDemoChatTtlOptionsEnabled;
    },

    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(presenter.groupId);
    },

    get isAutoCleanupDisabled(): boolean {
      return !presenter.group?.ttlPeriodMs;
    },

    get isCanEdit(): boolean {
      if (presenter.group?.isP2P) {
        return true;
      }
      return !!presenter.group?.isOwner || !!presenter.group?.isAdmin;
    },

    get settingsItems(): TChatAutoCleanupSettingsItem[] {
      return [
        {
          key: 'off',
          value: 0,
          isVisible: !presenter.group?.isSecretGroup,
        },
        {
          key: '2-minutes',
          value: 2 * ONE_MINUTE,
          isVisible: presenter.isDemoChatTtlOptionsEnabled,
        },
        {
          key: '5-minutes',
          value: 5 * ONE_MINUTE,
          isVisible: presenter.isDemoChatTtlOptionsEnabled,
        },
        {
          key: '10-minutes',
          value: 10 * ONE_MINUTE,
          isVisible: presenter.isDemoChatTtlOptionsEnabled,
        },
        {
          key: '24-hours',
          value: 24 * ONE_HOUR,
          isVisible: true,
        },
        {
          key: '2-days',
          value: 2 * ONE_DAY,
          isVisible: true,
        },
        {
          key: '3-days',
          value: 3 * ONE_DAY,
          isVisible: true,
        },
        {
          key: '7-days',
          value: 7 * ONE_DAY,
          isVisible: true,
        },
        {
          key: '30-days',
          value: 30 * ONE_DAY,
          isVisible: true,
        },
        {
          key: '2-months',
          value: 2 * ONE_AVERAGE_MONTH,
          isVisible: true,
        },
        {
          key: '3-months',
          value: 3 * ONE_AVERAGE_MONTH,
          isVisible: true,
        },
        {
          key: '6-months',
          value: 6 * ONE_AVERAGE_MONTH,
          isVisible: !presenter.group?.isSecretGroup,
        },
        {
          key: '1-year',
          value: ONE_AVERAGE_YEAR,
          isVisible: !presenter.group?.isSecretGroup,
        },
      ]
        .filter((item) => item.isVisible)
        .map((item) => ({
          ...item,
          title: getTtlPeriodText(item.value),
        }));
    },

    setGroupId(groupId: string): void {
      presenter.groupId = groupId;
    },
  }));

  useEffect(() => {
    presenter.setGroupId(params.groupId);
  }, [params.groupId]);
  return presenter;
}
